import type { ReactNode } from 'react';

import { cn, COLORS } from '#/lib/utils';

import {
  AgentIcon,
  CheckIcon,
  DecisionIcon,
  PlusIcon,
  TranscendMark,
  UserIcon,
} from './icons';

/** Resting colour for connectors and card borders before the pulse passes. */
export const LINE_COLOR = COLORS.grayscale['04'];

export const AGENT_LABELS = [
  'Agent Apps',
  'Custom agents',
  'Rails agents',
] as const;

export const RULE_LABELS = [
  'Consent & preferences',
  'Jurisdictional regulation',
  'Data subject requests',
  'Business policy',
] as const;

export const ACTIVATIONS = [
  'Budget limits',
  'Token usage',
  'Workflow automation',
  'Personalized customer experiences',
  'Recommendation engines',
  'Audience activation',
] as const;

type RectSize = { w: number; h: number; rx: number };

// Fixed card geometry so DrawSVG percentages stay exact. Height is a bit
// taller than Figma's frame so the four Rules pills clear the bottom edge.
export const INPUT_RECT: RectSize = { w: 536, h: 400, rx: 16 };
export const DECISION_RECT: RectSize = { w: 274, h: 400, rx: 16 };
export const ACTIVATE_RECT: RectSize = { w: 236, h: 400, rx: 16 };

/** Shared width for the stacked mobile cards. */
export const MOBILE_CARD_W = 343;
export const MOBILE_INPUT_RECT: RectSize = {
  w: MOBILE_CARD_W,
  h: 400,
  rx: 16,
};
/** Compact height — no desktop-sized empty gap under the logo. */
export const MOBILE_DECISION_RECT: RectSize = {
  w: MOBILE_CARD_W,
  h: 220,
  rx: 16,
};
export const MOBILE_ACTIVATE_RECT: RectSize = {
  w: MOBILE_CARD_W,
  h: 360,
  rx: 16,
};

/**
 * Where each edge midpoint sits along a `CardBorder` path, as a % of the
 * perimeter. The path starts at the top-centre and runs clockwise.
 */
export const EDGE = { top: 0, right: 25, bottom: 50, left: 75 } as const;

function roundedRectPath({ w, h, rx }: RectSize, inset: number) {
  const x0 = inset;
  const y0 = inset;
  const x1 = w - inset;
  const y1 = h - inset;
  const arc = (x: number, y: number, sweep = 1) =>
    `A${rx} ${rx} 0 0 ${sweep} ${x} ${y}`;
  return [
    `M${w / 2} ${y0}`,
    `H${x1 - rx}`,
    arc(x1, y0 + rx),
    `V${y1 - rx}`,
    arc(x1 - rx, y1),
    `H${x0 + rx}`,
    arc(x0, y1 - rx),
    `V${y0 + rx}`,
    arc(x0 + rx, y0),
    'Z',
  ].join(' ');
}

function CardBorder({
  id,
  rect,
  restingStroke = LINE_COLOR,
  litClassName,
}: {
  id: string;
  rect: RectSize;
  restingStroke?: string;
  litClassName?: string;
}) {
  const d = roundedRectPath(rect, 0.75);
  return (
    <svg
      className="pointer-events-none absolute inset-0"
      width={rect.w}
      height={rect.h}
      viewBox={`0 0 ${rect.w} ${rect.h}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d={d} stroke={restingStroke} strokeWidth={1} />
      <path
        id={id}
        d={d}
        className={cn('stroke-electric-04 svg-draw', litClassName)}
        strokeWidth={1.5}
      />
    </svg>
  );
}

function ColumnLabel({ children }: { children: ReactNode }) {
  return (
    <p className="font-azeret text-12 font-medium uppercase leading-none tracking-[0.96px] text-electric">
      {children}
    </p>
  );
}

function Pill({ label }: { label: string }) {
  return (
    <div className="flex w-full items-center justify-center rounded-8 border border-grayscale-05 px-12 py-8 text-center text-13 leading-140 text-cloud">
      {label}
    </div>
  );
}

export function Connector({
  id,
  orientation,
  className,
}: {
  id: string;
  orientation: 'horizontal' | 'vertical';
  className?: string;
}) {
  const horizontal = orientation === 'horizontal';
  const d = horizontal ? 'M0 1H100' : 'M1 0V100';
  return (
    <svg
      viewBox={horizontal ? '0 0 100 2' : '0 0 2 100'}
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn(
        horizontal ? 'h-[2px] shrink-0' : 'w-[2px] shrink-0',
        className,
      )}
    >
      <path d={d} stroke={LINE_COLOR} />
      <path
        d={d}
        id={id}
        className="stroke-electric-04 svg-draw"
        strokeWidth={2}
      />
    </svg>
  );
}

export function AgentsRulesCard({
  borderId,
  rect = INPUT_RECT,
}: {
  borderId: string;
  rect?: RectSize;
}) {
  const compact = rect.w < INPUT_RECT.w;
  return (
    <div
      className={cn(
        'relative flex shrink-0 items-center rounded-16 bg-charcoal',
        compact ? 'gap-4 px-12' : 'gap-8',
      )}
      style={{ width: rect.w, height: rect.h }}
    >
      <CardBorder id={borderId} rect={rect} />

      <div
        className={cn(
          'flex h-full min-w-0 flex-1 flex-col items-center',
          compact ? 'gap-16 py-24' : 'gap-28 px-24 py-32',
        )}
      >
        <ColumnLabel>Agents</ColumnLabel>
        <div className="flex size-[42px] items-center justify-center text-cipher-04">
          <div className="relative flex size-[42px] items-center justify-center">
            <svg
              className="absolute inset-0"
              viewBox="0 0 42.85 42.85"
              fill="none"
              aria-hidden
            >
              <circle
                cx="21.425"
                cy="21.425"
                r="21"
                stroke="currentColor"
                strokeWidth={0.85}
              />
            </svg>
            <AgentIcon className="relative size-[21px]" />
          </div>
        </div>
        <div className="flex w-full flex-col gap-12">
          {AGENT_LABELS.map((label) => (
            <Pill key={label} label={label} />
          ))}
        </div>
      </div>

      <PlusIcon
        className={cn(
          'shrink-0 text-grayscale-03',
          compact ? 'size-24' : 'size-32',
        )}
        aria-hidden
      />

      <div
        className={cn(
          'flex h-full min-w-0 flex-1 flex-col items-center',
          compact ? 'gap-16 py-24' : 'gap-28 px-24 py-32',
        )}
      >
        <ColumnLabel>Rules</ColumnLabel>
        <div
          className={cn('flex text-cipher-04', compact ? 'gap-8' : 'gap-12')}
        >
          {[0, 1, 2].map((i) => (
            <UserIcon
              key={i}
              className={compact ? 'size-32' : 'size-[42px]'}
              aria-hidden
            />
          ))}
        </div>
        <div className="flex w-full flex-col gap-12">
          {RULE_LABELS.map((label) => (
            <Pill key={label} label={label} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function DecisionCard({
  borderId,
  rect = DECISION_RECT,
}: {
  borderId: string;
  rect?: RectSize;
}) {
  const compact = rect.h < DECISION_RECT.h;
  return (
    <div
      className={cn(
        'relative flex shrink-0 flex-col items-center overflow-hidden rounded-16 bg-charcoal',
        compact ? 'gap-24 px-24 py-24' : 'gap-79 px-24 py-32',
      )}
      style={{ width: rect.w, height: rect.h }}
    >
      <CardBorder id={borderId} rect={rect} />
      <ColumnLabel>Real-time decisions</ColumnLabel>
      <div
        className={cn(
          'flex w-full flex-col items-center',
          compact ? 'gap-20' : 'gap-41',
        )}
      >
        <TranscendMark
          className={cn(
            'text-cloud',
            compact ? 'h-48 w-48' : 'h-[76px] w-[75px]',
          )}
          role="img"
          aria-label="Transcend"
        />
        <div className="flex w-full items-center gap-12 rounded-16 bg-grayscale-06 px-16 py-14">
          <div className="relative flex size-[42px] shrink-0 items-center justify-center text-cipher-04">
            <svg
              className="absolute inset-0"
              viewBox="0 0 42.85 42.85"
              fill="none"
              aria-hidden
            >
              <circle
                cx="21.425"
                cy="21.425"
                r="21"
                stroke="currentColor"
                strokeWidth={0.85}
              />
            </svg>
            <DecisionIcon className="relative size-[21px]" />
          </div>
          <div className="flex flex-col gap-4">
            <p className="whitespace-nowrap text-16 leading-113 text-grayscale-01">
              Decision API
            </p>
            <p className="whitespace-nowrap text-13 leading-140 text-grayscale-03">
              Enforce + audit
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ActivateCard({
  borderId,
  litCount,
  rect = ACTIVATE_RECT,
}: {
  borderId: string;
  litCount: number;
  rect?: RectSize;
}) {
  return (
    <div
      className="relative flex shrink-0 flex-col justify-between overflow-hidden rounded-16 bg-charcoal px-24 py-32"
      style={{ width: rect.w, height: rect.h }}
    >
      <CardBorder id={borderId} rect={rect} />
      <ColumnLabel>Activate agents & data responsibly</ColumnLabel>
      {ACTIVATIONS.map((label, index) => (
        <div
          key={label}
          className={cn(
            'flex w-full items-center gap-8 transition-opacity duration-300',
            index < litCount ? 'opacity-100' : 'opacity-40',
          )}
        >
          <CheckIcon
            className={cn(
              'size-24 shrink-0 transition-colors duration-300',
              index < litCount ? 'text-cipher-04' : 'text-grayscale-05',
            )}
            aria-hidden
          />
          <p className="min-w-0 text-13 font-medium leading-140 text-cloud">
            {label}
          </p>
        </div>
      ))}
    </div>
  );
}
